import { useCallback, useMemo, useState } from 'react';

import useTopics from '~hooks/useTopics';
import { unique } from '~utils/array';
import { TopicBubbles } from './topic-selector/TopicBubbles';
import { SubTopicSelector } from './SubTopicSelector';

type CountsShape = Record<TopicId, Array<TopicId>>;

function generateCounts(ids: Array<TopicId>, getTopicById: (id: TopicId) => Topic) {
  const counts: CountsShape = {};
  let topic: Topic;
  for (const id of ids) {
    topic = getTopicById(id);
    if (topic.parentId) {
      if (!counts[topic.parentId]) {
        counts[topic.parentId] = [topic.id];
      } else {
        counts[topic.parentId] = [...counts[topic.parentId], topic.id];
      }
    } else {
      counts[topic.id] = [];
    }
  }
  return counts;
}

//
// Counts
// Example Record
// {
//   <A>: [],
//   <B>: [<M>,<L>]
// }
// In the example above the following is true:
// - A is selected, but no subtopics of A are
// - B is selected and its subtopics M and L are selected as well
// - C is not selected (it's not even in the record)
//

type TopicSelectorProps = {
  topicIds: TopicId[];
  onChange: (topicIds: TopicId[]) => void;
};

export function TopicSelector({ topicIds, onChange }: TopicSelectorProps) {
  const { rootIds, getTopicById } = useTopics();
  const rootTopics = useMemo(() => rootIds.map(getTopicById), [getTopicById, rootIds]);
  const [activeTopicId, setActiveTopicId] = useState<TopicId>();
  const counts = useMemo(() => generateCounts(topicIds, getTopicById), [topicIds, getTopicById]);

  const addId = useCallback(
    function addId(id: TopicId) {
      onChange(unique([...topicIds, id]));
    },
    [onChange, topicIds],
  );
  const removeId = useCallback(
    function removeId(id: TopicId) {
      onChange(unique(topicIds.filter(selectedId => selectedId !== id)));
    },
    [onChange, topicIds],
  );

  const handleMainTopicClick = useCallback(
    (id: TopicId) => {
      const isActive = activeTopicId === id;
      setActiveTopicId(isActive ? undefined : id);
    },
    [activeTopicId],
  );

  const handleSubTopicClick = useCallback(
    (id: TopicId) => {
      // check if already selected
      const isSelected = topicIds.includes(id);
      if (!isSelected) {
        addId(id);
      } else {
        removeId(id);
      }
    },
    [addId, removeId, topicIds],
  );

  return (
    <div className="relative w-full h-full flex flex-col">
      <TopicBubbles
        topics={rootTopics}
        onTopicClick={handleMainTopicClick}
        activeTopicId={activeTopicId}
        counts={counts}
      />
      <SubTopicSelector
        topic={getTopicById(activeTopicId)}
        onTopicClick={handleSubTopicClick}
        selectedTopicIds={topicIds}
      />
    </div>
  );
}
